// Обращения водителя.
//
// Живой чат — одна ветка на человека, лежит под его uid: правило отдаёт
// ровно свою переписку и ничего чужого. Zgłoszenia — то, что решается
// дольше одного сообщения; их заводит водитель или партнёр.
//
// Раньше всё лежало в RTDB одним списком чатов компании, а свои сообщения
// панель отбирала фильтром в браузере: через консоль читалась переписка
// всех коллег.

import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
    addDoc, collection, doc, onSnapshot, orderBy, query,
    serverTimestamp, setDoc, updateDoc, where
} from 'firebase/firestore';
import { ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import { Button, Input, Modal, Upload, message as antMessage } from 'antd';
import { UploadOutlined, CloseCircleOutlined, FileOutlined } from '@ant-design/icons';
import { auth, db, storage } from '../firebaseConfig';
import { useCompany } from '../contexts/CompanyContext';
import './Pomoc.css';

const STATUS_LABEL = {
    new: 'Nowe',
    in_progress: 'W trakcie',
    waiting: 'Czeka na Ciebie',
    closed: 'Zamknięte'
};

// Время приходит то как Timestamp, то строкой из перенесённых чатов.
const asDate = (value) => {
    if (!value) return null;
    if (value.toDate) return value.toDate();
    const date = new Date(value);
    return isNaN(date.getTime()) ? null : date;
};

const clock = (value) => {
    const date = asDate(value);
    return date ? new Intl.DateTimeFormat('pl-PL', { hour: 'numeric', minute: 'numeric' }).format(date) : '';
};

const dayLabel = (value) => {
    const date = asDate(value);
    return date ? new Intl.DateTimeFormat('pl-PL',
        { year: 'numeric', month: 'long', day: 'numeric' }).format(date) : '';
};

// Асистента зовёт панель, а не триггер: база лежит в мультирегионе eur3
// и событий в Eventarc не отдаёт. Ответ приходит в ту же ветку, поэтому
// подписка на сообщения покажет его сама — ждать здесь нечего.
const ASSISTANT_FN = 'https://us-central1-godelta-partner-crm.cloudfunctions.net/askAssistant';

const askAssistant = async (user, companyId) => {
    try {
        const idToken = await user.getIdToken();
        await fetch(ASSISTANT_FN, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` },
            body: JSON.stringify({ companyId })
        });
    } catch (e) {
        // Агент — надстройка, а не условие работы чата.
    }
};

const fileNameOf = (url) => {
    try {
        const path = decodeURIComponent(String(url).split('?')[0]);
        const name = path.substring(path.lastIndexOf('/') + 1);
        return name.length > 24 ? `${name.slice(0, 18)}…${name.slice(-5)}` : name;
    } catch (e) {
        return 'Plik';
    }
};

const Pomoc = () => {
    const { companyId } = useCompany();
    const [user, setUser] = useState(auth.currentUser);

    const [tab, setTab] = useState('czat');
    const [threadMessages, setThreadMessages] = useState([]);
    const [tickets, setTickets] = useState([]);
    const [openTicket, setOpenTicket] = useState(null);
    const [ticketMessages, setTicketMessages] = useState([]);

    const [text, setText] = useState('');
    const [file, setFile] = useState(null);
    const [preview, setPreview] = useState(null);
    const [sending, setSending] = useState(false);
    const [creating, setCreating] = useState(false);
    const [newSubject, setNewSubject] = useState('');
    const [zoom, setZoom] = useState(null);

    const endRef = useRef(null);

    useEffect(() => auth.onAuthStateChanged(setUser), []);

    useEffect(() => {
        if (!companyId || !user) return undefined;
        const unsub = onSnapshot(
            query(collection(db, 'companies', companyId, 'threads', user.uid, 'messages'),
                  orderBy('createdAt', 'asc')),
            (snap) => setThreadMessages(snap.docs.map(d => ({ id: d.id, ...d.data() }))),
            () => setThreadMessages([])
        );
        return () => unsub();
    }, [companyId, user]);

    // Свои zgłoszenia. Запрос обязан быть сужен по себе — иначе правило
    // чтения его не пропустит.
    useEffect(() => {
        if (!companyId || !user) return undefined;
        const unsub = onSnapshot(
            query(collection(db, 'companies', companyId, 'tickets'),
                  where('driverUid', '==', user.uid)),
            (snap) => setTickets(snap.docs.map(d => ({ id: d.id, ...d.data() }))),
            () => setTickets([])
        );
        return () => unsub();
    }, [companyId, user]);

    useEffect(() => {
        if (!companyId || !openTicket) { setTicketMessages([]); return undefined; }
        const unsub = onSnapshot(
            query(collection(db, 'companies', companyId, 'tickets', openTicket.id, 'messages'),
                  orderBy('createdAt', 'asc')),
            (snap) => setTicketMessages(snap.docs.map(d => ({ id: d.id, ...d.data() }))),
            () => setTicketMessages([])
        );
        return () => unsub();
    }, [companyId, openTicket]);

    // Открыл чат — значит прочитал.
    useEffect(() => {
        if (!companyId || !user || tab !== 'czat') return;
        setDoc(doc(db, 'companies', companyId, 'threads', user.uid),
               { unreadForDriver: 0 }, { merge: true }).catch(() => {});
    }, [companyId, user, tab, threadMessages.length]);

    useEffect(() => {
        setTimeout(() => endRef.current?.scrollIntoView({ behavior: 'smooth' }), 80);
    }, [threadMessages, ticketMessages, tab, openTicket]);

    const sortedTickets = useMemo(() => [...tickets].sort((a, b) => {
        const aClosed = a.status === 'closed' ? 1 : 0;
        const bClosed = b.status === 'closed' ? 1 : 0;
        if (aClosed !== bClosed) return aClosed - bClosed;
        return (asDate(b.updatedAt || b.createdAt)?.getTime() || 0)
             - (asDate(a.updatedAt || a.createdAt)?.getTime() || 0);
    }), [tickets]);

    const pickFile = (chosen) => {
        setFile(chosen);
        setPreview(chosen && chosen.type?.startsWith('image/') ? URL.createObjectURL(chosen) : null);
    };

    const dropFile = () => { setFile(null); setPreview(null); };

    const uploadAttachment = async (kind) => {
        if (!file) return null;
        // Владелец вложения виден прямо из пути: правила Storage не умеют
        // читать базу и определяют его только так.
        const path = `chatFiles/${companyId}/${kind}/${user.uid}/${Date.now()}_${file.name}`;
        const reference = storageRef(storage, path);
        await uploadBytes(reference, file);
        return { url: await getDownloadURL(reference), type: file.type };
    };

    const send = async () => {
        if ((!text.trim() && !file) || !companyId || !user) return;

        setSending(true);
        try {
            const inTicket = tab === 'zgloszenia' && openTicket;
            const attachment = await uploadAttachment(inTicket ? 'ticket' : 'thread');
            const payload = {
                from: 'driver',
                authorUid: user.uid,
                authorName: user.displayName || user.email || '',
                text: text.trim(),
                fileUrl: attachment?.url || null,
                fileType: attachment?.type || null,
                createdAt: serverTimestamp()
            };

            if (inTicket) {
                await addDoc(collection(db, 'companies', companyId, 'tickets', openTicket.id, 'messages'), payload);
                await updateDoc(doc(db, 'companies', companyId, 'tickets', openTicket.id), {
                    lastMessageText: text.trim().slice(0, 200) || 'Załącznik',
                    updatedAt: serverTimestamp()
                }).catch(() => {});
            } else {
                await addDoc(collection(db, 'companies', companyId, 'threads', user.uid, 'messages'), payload);
                await setDoc(doc(db, 'companies', companyId, 'threads', user.uid), {
                    driverUid: user.uid,
                    lastMessageText: text.trim().slice(0, 200) || 'Załącznik',
                    lastMessageAt: serverTimestamp(),
                    lastMessageFrom: 'driver',
                    unreadForDriver: 0
                }, { merge: true });

                // Если у партнёра подключён асистент, он ответит сразу.
                // Молчание тут не ошибка: агент может быть выключен или
                // занят — тогда сообщение просто ждёт человека.
                askAssistant(user, companyId);
            }

            setText('');
            dropFile();
        } catch (e) {
            antMessage.error('Nie udało się wysłać wiadomości');
        } finally {
            setSending(false);
        }
    };

    const createTicket = async () => {
        if (!newSubject.trim() || !companyId || !user) return;
        setSending(true);
        try {
            const created = await addDoc(collection(db, 'companies', companyId, 'tickets'), {
                subject: newSubject.trim(),
                driverUid: user.uid,
                driverName: user.displayName || user.email || '',
                status: 'new',
                assignedTo: null,
                source: 'driver',
                createdBy: user.uid,
                createdAt: serverTimestamp(),
                updatedAt: serverTimestamp(),
                lastMessageText: ''
            });
            setCreating(false);
            setNewSubject('');
            setOpenTicket({ id: created.id, subject: newSubject.trim(), status: 'new' });
            setTab('zgloszenia');
        } catch (e) {
            antMessage.error('Nie udało się utworzyć zgłoszenia');
        } finally {
            setSending(false);
        }
    };

    if (!companyId || !user) {
        return <div className="pomoc-empty">Zaloguj się, żeby napisać do biura.</div>;
    }

    const messages = tab === 'czat' ? threadMessages : ticketMessages;
    const canWrite = tab === 'czat' || (openTicket && openTicket.status !== 'closed');

    const feed = [];
    let lastDay = '';
    messages.forEach(msg => {
        const day = dayLabel(msg.createdAt);
        if (day && day !== lastDay) {
            feed.push(<div key={`d-${day}-${msg.id}`} className="date-separator">{day}</div>);
            lastDay = day;
        }
        const mine = msg.from === 'driver';
        feed.push(
            <div key={msg.id} className={`pomoc-msg ${mine ? 'mine' : 'theirs'}`}>
                <div className="pomoc-bubble">
                    {msg.from === 'ai' && <div className="pomoc-ai">Asystent</div>}
                    {msg.text && <p>{msg.text}</p>}
                    {msg.fileUrl && msg.fileType?.startsWith('image/') && (
                        <img src={msg.fileUrl} alt="" onClick={() => setZoom(msg.fileUrl)}/>
                    )}
                    {msg.fileUrl && !msg.fileType?.startsWith('image/') && (
                        <a href={msg.fileUrl} target="_blank" rel="noreferrer">
                            <FileOutlined/> {fileNameOf(msg.fileUrl)}
                        </a>
                    )}
                    <span className="pomoc-time">{clock(msg.createdAt)}</span>
                </div>
            </div>
        );
    });

    return (
        <div className="pomoc">
            <div className="pomoc-tabs">
                <button className={tab === 'czat' ? 'on' : ''}
                        onClick={() => { setTab('czat'); setOpenTicket(null); }}>
                    Czat
                </button>
                <button className={tab === 'zgloszenia' ? 'on' : ''}
                        onClick={() => setTab('zgloszenia')}>
                    Zgłoszenia
                </button>
            </div>

            {tab === 'zgloszenia' && !openTicket && (
                <div className="pomoc-tickets">
                    <Button type="primary" onClick={() => setCreating(true)}>Nowe zgłoszenie</Button>

                    {sortedTickets.length === 0 && (
                        <div className="pomoc-empty">
                            Nie masz jeszcze zgłoszeń. Sprawy pilne pisz na czacie.
                        </div>
                    )}

                    {sortedTickets.map(ticket => (
                        <button key={ticket.id} className="pomoc-ticket" onClick={() => setOpenTicket(ticket)}>
                            <span className="pomoc-ticket-subject">{ticket.subject}</span>
                            <span className={`pomoc-ticket-status s-${ticket.status}`}>
                                {STATUS_LABEL[ticket.status] || ticket.status}
                            </span>
                            <span className="pomoc-ticket-time">
                                {dayLabel(ticket.updatedAt || ticket.createdAt)}
                            </span>
                        </button>
                    ))}
                </div>
            )}

            {(tab === 'czat' || openTicket) && (
                <>
                    {openTicket && (
                        <div className="pomoc-head">
                            <button className="pomoc-back" onClick={() => setOpenTicket(null)}>← Wróć</button>
                            <span>{openTicket.subject}</span>
                        </div>
                    )}

                    <div className="pomoc-feed">
                        {messages.length === 0 && (
                            <div className="pomoc-empty">
                                {tab === 'czat'
                                    ? 'Napisz do biura — odpowiemy tutaj.'
                                    : 'Brak wiadomości w tym zgłoszeniu.'}
                            </div>
                        )}
                        {feed}
                        <div ref={endRef}/>
                    </div>

                    {canWrite ? (
                        <div className="pomoc-composer">
                            {preview && (
                                <div className="image-preview">
                                    <img src={preview} alt="" onClick={dropFile}/>
                                    <CloseCircleOutlined onClick={dropFile}/>
                                </div>
                            )}
                            {file && !preview && (
                                <div className="file-preview">
                                    <FileOutlined/> <span>{file.name}</span>
                                    <CloseCircleOutlined onClick={dropFile}/>
                                </div>
                            )}

                            <Input.TextArea rows={2}
                                            value={text}
                                            placeholder="Napisz wiadomość…"
                                            onChange={e => setText(e.target.value)}
                                            onPressEnter={e => { if (!e.shiftKey) { e.preventDefault(); send(); } }}/>

                            <div className="pomoc-actions">
                                <Upload beforeUpload={(chosen) => { pickFile(chosen); return false; }}
                                        showUploadList={false}
                                        maxCount={1}>
                                    <Button icon={<UploadOutlined/>}/>
                                </Upload>
                                <Button type="primary" loading={sending} onClick={send}>Wyślij</Button>
                            </div>
                        </div>
                    ) : (
                        <div className="pomoc-empty">Zgłoszenie zamknięte.</div>
                    )}
                </>
            )}

            <Modal open={creating}
                   title="Nowe zgłoszenie"
                   okText="Utwórz"
                   cancelText="Anuluj"
                   confirmLoading={sending}
                   onOk={createTicket}
                   onCancel={() => setCreating(false)}>
                <Input placeholder="Czego dotyczy sprawa?"
                       value={newSubject}
                       onChange={e => setNewSubject(e.target.value)}/>
            </Modal>

            <Modal open={Boolean(zoom)} footer={null} onCancel={() => setZoom(null)} style={{ top: 20 }}>
                <img src={zoom} alt="" style={{ width: '100%' }}/>
                <Button type="primary" href={zoom} download style={{ marginTop: 10 }}>Pobierz</Button>
            </Modal>
        </div>
    );
};

export default Pomoc;
